import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import AsyncSelect, {
  AsyncSelectValue,
} from "../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomSelectScroll from "@/components/CustomSelectScroll";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import OutlineCard from "../../../../../components/OutlineCard";
import ProviderSelectScroll from "../../../../../components/ProviderSelectScroll";
import Text from "../../../../../components/Text";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useGetInsuranceByPatientId } from "../../../../../queries/insurance/use-get-patient-insurance-patient-uuid";
import { useGetAppointment } from "../../../../../queries/use-get-appointment";
import { useSearchPatientList } from "../../../../../queries/list/useSearchPatientList";
import { useAppointmentsV2 } from "../../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import {
  formatDate,
  formatDateTime,
} from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getFormattedId,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import { placesOfService } from "../add-super-bill-components/places-of-service";
import { isTelehealthPlaceOfService } from "../use-service-location-billing";
import { useServiceLocationOptions } from "../use-service-location-options";
import { CLAIM_FORM_TYPE, INITIALVALUES } from "./claims-constants";
import { useProviderNpiAutofill } from "./updateClaims/use-provider-npi-autofill";
import { getProviderGroupName } from "@/utils/auth-storage";

interface UpsertClaimProps {
  formik: FormikProps<CLAIM_FORM_TYPE>;
  // Locked when the claim is launched against a specific patient/visit. The
  // standalone "Create New Claim" entry has no patient yet, so it stays open.
  isPatientLocked?: boolean;
}

const UpsertClaim = ({ formik, isPatientLocked = false }: UpsertClaimProps) => {
  const { values, setFieldValue } = formik;

  // Billing Provider is the Practice Group (Billing Entity), not an
  // individual provider. The practice name is the tenant identifier; the
  // resolved practice details (NPI, contact, billing address) come back from
  // the claim once it is created.
  const practiceName = getProviderGroupName() ?? undefined;

  // Referring Provider NPI is optional and auto-filled from the selected
  // provider's profile, while staying editable.
  const { markSelected: markReferringProviderSelected } =
    useProviderNpiAutofill({
      providerUuid: values.referringProvider?.value,
      tenantId: getXtenantId(),
      currentValue: values.referringProviderNPINumber,
      setValue: (npi) => setFieldValue("referringProviderNPINumber", npi),
    });

  // Keeps the Location prefilled from the selected visit selectable even when
  // it falls outside the page the Locations search currently returns.
  const {
    options: serviceLocationOptions,
    onSearch: onServiceLocationSearch,
    toServiceLocation,
    pagination: serviceLocationPagination,
    setPagination: setServiceLocationPagination,
    totalElements: serviceLocationTotalElements,
    loading: serviceLocationLoading,
  } = useServiceLocationOptions(values.serviceLocation);

  const [visitSearch, setVisitSearch] = useDebounce<string>("");
  const [patientSearch, setPatientSearch] = useDebounce<string>("");
  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [visitPagination, setVisitPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: patientList, isFetching: isFetchingPatients } =
    useSearchPatientList({
      pagination: patientPagination,
      searchString: patientSearch,
      xTENANTID: getXtenantId(),
    });

  const {
    data: patientInsuraneData,
    isLoading: isLoadingInsurance,
    isFetching: isFetchingInsurance,
  } = useGetInsuranceByPatientId(getValueFromObj(formik?.values?.patient));

  const {
    data: patientEncounterList,
    isLoading: isLoadingEncounterList,
    isFetching: isFetchingEncounterList,
  } = useAppointmentsV2({
    searchKey: visitSearch,
    pagination: visitPagination,
    appointmentStatus: ["COMPLETED"],
    // A partially billed visit still has unbilled CPT codes left to claim, so it
    // must stay selectable here — filtering on NOT_BILLED alone hid it and left
    // no way to claim the remaining codes. Comma-joined, same convention as the
    // invoice visit dropdown and the Ready For Billing filter.
    billedStatus: "NOT_BILLED,PARTIALLY_BILLED",
    // Self Pay and Out of Network visits are invoiced, not claimed, so the
    // backend keeps them out of this dropdown.
    claimEligible: true,
    // superBillGenerated: false,
    patientUuid: formik.values.patient?.value
      ? [String(formik.values.patient.value)]
      : undefined,
    disabled: !formik.values.patient?.value,
  });

  // Full appointment for the selected visit. The light list above does NOT
  // carry the scheduling insurance, so we fetch the appointment by id to read
  // the insurance the user selected while scheduling this specific visit.
  const selectedEncounterUuid = formik.values.encounter?.value
    ? String(formik.values.encounter.value)
    : undefined;

  // `isPlaceholderData` matters here: the query keeps the previous appointment
  // (keepPreviousData), so `data` still holds the last visit's appointment
  // after the visit is removed or while a different one loads.
  const { data: selectedAppointment, isPlaceholderData: isAppointmentStale } =
    useGetAppointment({
      appointmentUuid: selectedEncounterUuid,
      providerGroupId: getXtenantId() ?? "",
      disabled: !selectedEncounterUuid,
    });

  const encounterHandler = (value: any) => {
    // Find the encounter object by uuid
    const encounterObj = patientEncounterList?.data?.content?.find(
      (encounter: any) => encounter.uuid === value?.value
    );
    // Now encounterObj holds the full encounter object or undefined if not found
    formik.setFieldValue("reasonForVisit", encounterObj?.chiefComplaint ?? "");

    // to-do : location data need to test whether it is populating or not.
    formik.setFieldValue(
      "serviceLocation",
      encounterObj?.locationId
        ? {
            [encounterObj?.locationId]: encounterObj?.locationName ?? "",
          }
        : undefined
    );

    formik.setFieldValue(
      "dateOfService",
      encounterObj?.startTime ?? encounterObj?.encounterDate
    );

    formik.setFieldValue(
      "provider",
      encounterObj?.providerId
        ? { value: encounterObj?.providerId, label: encounterObj?.providerName }
        : undefined
    );

    // condition for place of service  mode - "IN_PERSON" | "VIRTUAL"
    // condition for place of service  "IN_PERSON" | "VIRTUAL"
    // mode

    if (encounterObj?.mode === "VIRTUAL") {
      formik.setFieldValue("placeOfService", "TELEHEALTH_INSIDE_PATIENT_HOME");
    } else if (encounterObj?.mode === "IN_PERSON") {
      formik.setFieldValue("placeOfService", "OFFICE");
    } else {
      formik.setFieldValue("placeOfService", undefined);
    }

    // NOTE: Payer Insurance is auto-populated by the dedicated useEffect below
    // using the full appointment fetch (getAppointmentById). The encounter
    // light list does not carry patientPrimaryInsurance, so this block always
    // no-oped. Kept commented for discoverability.
    // if (encounterObj?.patientPrimaryInsurance) {
    //   formik.setFieldValue("payerInsuance", {
    //     value: encounterObj?.patientPrimaryInsurance?.uuid ?? undefined,
    //     label:
    //       encounterObj?.patientPrimaryInsurance?.insurancePayer?.payerName ??
    //       undefined,
    //     insuranceId:
    //       encounterObj?.patientPrimaryInsurance?.insuranceId ?? undefined,
    //   });
    //   formik.setFieldValue(
    //     "insuranceId",
    //     encounterObj?.patientPrimaryInsurance?.insuranceId ?? undefined
    //   );
    //   formik.setFieldValue("patientInsurance", {
    //     uuid: encounterObj?.patientPrimaryInsurance?.uuid ?? undefined,
    //   });
    // }

    formik.setFieldValue("encounterType", encounterObj?.type ?? undefined);
    formik.setFieldValue("reasonForVisit", encounterObj?.chiefComplaint);
  };

  const [isHovered, setIsHovered] = useState<string>();

  // Payer Insurance and Insurance Id are derived from the selected visit, so
  // they have to be dropped whenever that selection changes. The auto-populate
  // effect below then refills them from the new visit, or from the patient's
  // primary insurance once no visit is selected.
  const clearInsuranceFields = () => {
    formik.setFieldValue("payerInsuance", undefined);
    formik.setFieldValue("insuranceId", "");
    formik.setFieldValue("patientInsurance", undefined);
  };

  const prevEncounterRef = useRef(formik.values.encounter);

  // THIS useEffect IS FOR HANDLING THE ENCOUNTER HANDLER
  useEffect(() => {
    // The handler also has to re-run once the encounter list arrives, since it
    // reads the selected visit out of that list.
    if (!patientEncounterList?.data?.content) return;

    const visitChanged = formik.values.encounter !== prevEncounterRef.current;
    prevEncounterRef.current = formik.values.encounter;

    encounterHandler(formik.values.encounter);

    if (visitChanged) clearInsuranceFields();
  }, [formik.values.encounter, patientEncounterList?.data?.content]);

  // Auto-populate Payer Insurance when nothing is selected yet. Priority:
  //   1. The insurance selected while scheduling THIS visit
  //      (appointment.patientPrimaryInsurance) — so the claim reflects the
  //      payer chosen on the scheduling screen for that specific appointment.
  //   2. The patient's primary active insurance — fallback for a fresh claim
  //      where the appointment has no insurance on file.
  // The field stays editable with all insurance options in the dropdown.
  useEffect(() => {
    // No patient, nothing to derive from. Without this the effect refilled the
    // insurance straight after the patient was removed, using the appointment
    // the query still held, and the stale value then blocked the next patient.
    if (!formik.values.patient?.value) return;

    if (formik.values.payerInsuance) return;

    // When a visit is selected, wait for its own appointment to load so the
    // scheduling insurance wins over the patient-level primary fallback, and
    // so the previous visit's retained appointment is never used.
    if (
      selectedEncounterUuid &&
      (isAppointmentStale || !selectedAppointment?.data)
    )
      return;

    const appointmentInsurance = selectedEncounterUuid
      ? selectedAppointment?.data?.patientPrimaryInsurance
      : undefined;

    const fallbackInsurance = patientInsuraneData?.content?.find(
      (it: any) => it?.active && it?.orderOfBenefits === "PRIMARY"
    );

    const defaultInsurance = appointmentInsurance ?? fallbackInsurance;
    if (!defaultInsurance?.uuid) return;

    formik.setFieldValue("payerInsuance", {
      value: defaultInsurance?.uuid,
      label: defaultInsurance?.insurancePayer?.payerName,
      insuranceId: defaultInsurance?.insuranceId,
    });
    formik.setFieldValue("insuranceId", defaultInsurance?.insuranceId ?? "");
    formik.setFieldValue("patientInsurance", {
      uuid: defaultInsurance?.uuid,
    });
  }, [
    selectedAppointment?.data,
    isAppointmentStale,
    selectedEncounterUuid,
    patientInsuraneData?.content,
    formik.values.patient?.value,
    formik.values.payerInsuance,
  ]);

  // console.log("val here check ", formik.values);

  // const [searchAppointmentTypeKey, setSearchAppointmentTypeKey] =
  //   useDebounce<string>("");
  // const {
  //   data: appointmentTypes,
  // } = useAppointmentTypeGetAll({
  //   pagination: { page: 0, pageSize: 30 },
  //   searchString: searchAppointmentTypeKey,
  //   archive: false,
  //   xTENANTID: getXtenantId(),
  // });

  // const appointmentTypeList = useMemo(
  //   () =>
  //     appointmentTypes?.data?.content?.map((it: any) => ({
  //       value: it?.nameString?.toUpperCase(),
  //       label: it?.nameString?.toUpperCase(),
  //     })) ?? [],
  //   [appointmentTypes?.data?.content]
  // );

  // console.log("val here check appointment list ", appointmentTypeList);

  return (
    <Box mt={1}>
      <Grid item container spacing={2.5}>
        {/* Patient Name */}

        <Grid item xs={6}>
          <AsyncSelectScroll
            enableFilterOption
            disabled={isPatientLocked}
            loading={isFetchingPatients}
            pagination={patientPagination}
            setPagination={setPatientPagination}
            totalElements={patientList?.data?.page?.totalElements}
            handleBlur={formik.handleBlur}
            onSearch={(val) => {
              setPatientSearch(val);
              setPatientPagination((p) => ({ ...p, page: 0 }));
            }}
            placeholder="Search Patient"
            label="Patient Name"
            required
            name="patient"
            renderCustomLabel={(option) => (
              <Flex flexDirection={"column"}>
                <Text>{option.label}</Text>
                <Text>
                  {option?.birthDate ? formatDate(option?.birthDate) : ""}
                </Text>
              </Flex>
            )}
            options={
              patientList?.data?.content?.map((p) => {
                return {
                  value: p.uuid,
                  label: `${p.firstName}  ${p.lastName}`,
                  birthDate: p?.birthDate,
                };
              }) ?? []
            }
            value={formik.values.patient as any}
            onChange={(value) => {
              if (!value) {
                formik.resetForm();
                return;
              }

              // A different patient invalidates the visit and every field
              // derived from it. setValues (not resetForm) so `initialValues`
              // is left alone — the form runs with enableReinitialize, which
              // would otherwise wipe the patient right back out.
              if (value?.value !== formik.values.patient?.value) {
                formik.setValues({ ...INITIALVALUES, patient: value as any });
                return;
              }

              formik.setFieldValue("patient", value);
            }}
            error={(formik.touched?.patient && formik.errors?.patient) as any}
          />
        </Grid>

        {/* Select Encounter */}
        <Grid item xs={6}>
          <AsyncSelectScroll
            enableFilterOption
            handleBlur={formik.handleBlur}
            pagination={visitPagination}
            setPagination={setVisitPagination}
            totalElements={patientEncounterList?.data?.page?.totalElements}
            onSearch={(val) => {
              setVisitSearch(val);
              setVisitPagination((p) => ({ ...p, page: 0 }));
            }}
            placeholder="Select Visit"
            label="Patient Visit"
            // required
            name="encounter"
            loading={isLoadingEncounterList || isFetchingEncounterList}
            renderCustomLabel={(option) => {
              const time = option?.startTime
                ? formatDateTime(option?.startTime)
                : "-";
              const HoveredBox = (option: any) =>
                `Appointment Type: ${replaceUnderScoreAddSpace(
                  option.label.split(" - ")[1]
                )}, Date & Time:${time}, Reason for Visit: ${
                  option.chiefComplaint || "-"
                }`;

              return (
                <Box
                  onMouseEnter={() => setIsHovered(option.uuid)}
                  onMouseLeave={() => setIsHovered(undefined)}
                >
                  <Text>{replaceUnderScoreAddSpace(option.label)}</Text>
                  {isHovered === option.uuid && (
                    <OutlineCard>{HoveredBox(option)}</OutlineCard>
                  )}
                </Box>
              );
            }}
            options={
              patientEncounterList?.data?.content?.map((option: any) => {
                const encounterDate =
                  option?.startTime ?? option?.encounterDate;
                const time = encounterDate
                  ? formatDateTime(encounterDate)
                  : "-";
                return {
                  value: option.uuid,
                  label: `${getFormattedId(option.uuid)} - ${
                    option?.type ? option?.type : ""
                  } - ${time}`,
                  startTime: encounterDate,
                  chiefComplaint: option?.chiefComplaint,
                  uuid: option.uuid,
                };
              }) ?? []
            }
            value={formik.values.encounter as any}
            onChange={(value) => {
              formik.setFieldValue("encounter", value);
              // encounterHandler(value);
            }}
            error={
              (formik.touched?.encounter && formik.errors?.encounter) as any
            }
          />
        </Grid>
        {/* Service Location */}
        {/* //to-do use async select */}
        <Grid item xs={4}>
          <CustomSelectScroll
            label="Service Location"
            required={!isTelehealthPlaceOfService(values.placeOfService)}
            placeholder="Select Service Location"
            options={serviceLocationOptions}
            name="serviceLocation"
            onSearch={(_, val) => onServiceLocationSearch(val)}
            value={Object.keys(values.serviceLocation ?? {})?.[0] ?? undefined}
            onChange={(value) =>
              setFieldValue("serviceLocation", toServiceLocation(value))
            }
            pagination={serviceLocationPagination}
            setPagination={setServiceLocationPagination}
            totalElements={serviceLocationTotalElements}
            loading={serviceLocationLoading}
            error={
              (formik.touched?.serviceLocation &&
                formik.errors?.serviceLocation) as any
            }
            handleBlur={formik.handleBlur}
          />
        </Grid>
        {/* Place of service  */}
        {/* //to-do use async select */}
        <Grid item xs={4}>
          <CustomSelect
            required
            error={
              formik.touched?.placeOfService && formik.errors.placeOfService
            }
            name="placeOfService"
            handleBlur={formik.handleBlur}
            label="Place Of Service"
            options={placesOfService}
            value={formik.values.placeOfService}
            onChange={(value) => {
              setFieldValue("placeOfService", value);
            }}
          />
        </Grid>
        {/* date Of Service */}
        <Grid item xs={4}>
          <CustomDatePicker
            required
            name="dateOfService"
            maxDate={dayjs(new Date())}
            label="Date Of Service"
            value={values.dateOfService}
            onChange={(value) => setFieldValue("dateOfService", value)}
            placeholder="Date Of Service"
            error={
              (formik.touched?.dateOfService &&
                formik.errors?.dateOfService) as any
            }
            handleBlur={formik.handleBlur}
          />
        </Grid>
        {/* Rendering Provider */}
        <Grid item xs={4}>
          <ProviderSelectScroll
            label="Rendering Provider"
            required
            name="provider"
            value={formik.values.provider as AsyncSelectValue}
            onChange={(val) => formik.setFieldValue("provider", val)}
            handleBlur={formik.handleBlur}
            error={(formik.touched?.provider && formik.errors?.provider) as any}
          />
        </Grid>
        {/* Referring Provider */}
        <Grid item xs={4}>
          <ProviderSelectScroll
            label="Referring Provider"
            name="referringProvider"
            handleBlur={formik.handleBlur}
            value={formik.values.referringProvider || null}
            onChange={(val) => {
              markReferringProviderSelected(val?.value ?? null);
              formik.setFieldValue("referringProvider", val);
              // Clearing the provider clears the NPI it filled in.
              if (!val) formik.setFieldValue("referringProviderNPINumber", "");
            }}
            error={
              (formik.touched?.referringProvider &&
                formik.errors?.referringProvider) as any
            }
          />
        </Grid>
        {/* Referring Provider NPI — optional, auto-filled from the provider
            profile and editable */}
        <Grid item xs={4}>
          <Input
            label="Referring Provider NPI"
            error={
              (formik.touched?.referringProviderNPINumber &&
                formik.errors?.referringProviderNPINumber) as any
            }
            textFieldProps={{
              name: "referringProviderNPINumber",
              placeholder: "Referring Provider NPI",
              value: formik.values.referringProviderNPINumber ?? "",
              onBlur: formik.handleBlur,
              onChange: (e) =>
                formik.setFieldValue(
                  "referringProviderNPINumber",
                  e.target.value
                ),
            }}
          />
        </Grid>
        {/* billingProvider — Practice Group (Billing Entity), read-only */}
        <Grid item xs={4}>
          <Input
            label="Billing Provider"
            textFieldProps={{
              name: "billingProvider",
              placeholder: "Billing Provider",
              disabled: true,
              value: practiceName || formik.values.billingProvider?.label || "",
            }}
          />
        </Grid>
        {/* Appoinment Type  */}
        {/* //to-do use async select */}
        {/* <Grid item xs={4}>
          <CustomSelect
            name="type"
            handleBlur={formik.handleBlur}
            label="Appoinment Type"
            options={NEW_APPOINTMENT_TYPE}
            placeholder="Select Appoinment Type"
            value={formik.values.encounterType ?? undefined}
            onChange={(value) => {
              formik.setFieldValue("encounterType", value);
            }}
            error={
              (formik.touched?.encounterType &&
                formik.errors?.encounterType) as any
            }
          /> */}

        <Grid item xs={4}>
          {/* <CustomSelect
            required
            error={formik.touched?.encounterType && formik.errors?.encounterType}
            name="encounterType"
            handleBlur={formik.handleBlur}
            label="Appointment Type"
            placeholder="Select Appointment Type"
            onSearch={(_, v) => setSearchAppointmentTypeKey(v)}
            options={appointmentTypeList}
            value={formik.values.encounterType}
            onChange={(value) =>
              formik.setFieldValue("encounterType", value)
            }
          /> */}
          <Input
            label="Appointment Type"
            required
            textFieldProps={{
              placeholder: "Please Select Visit",
              name: "encounterType",
              // `?? ""` keeps the field controlled. Clearing it set the value
              // to undefined, which flipped the input to uncontrolled and left
              // the previous visit's type sitting on screen.
              value: formik.values.encounterType ?? "",
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              disabled: true,
            }}
            error={
              formik.touched?.encounterType && formik.errors?.encounterType
            }
          />
        </Grid>
        {/* Payer insurance  */}
        <Grid item xs={6}>
          <AsyncSelect
            required
            loading={isLoadingInsurance ?? isFetchingInsurance}
            label="Payer Insurance"
            name="payerInsuance"
            options={patientInsuraneData?.content?.map((it: any) => ({
              value: it?.uuid,
              label: `${it?.insurancePayer?.payerName}`,
              insuranceId: it?.insuranceId,
            }))}
            enableFilterOption
            renderCustomLabel={(option) => (
              <Flex flexDirection={"column"}>
                <Text>{option.label}</Text>
              </Flex>
            )}
            handleBlur={formik.handleBlur}
            // onSearch={setSearchCodeKey}
            placeholder="Search & Select Insurance"
            value={formik.values.payerInsuance}
            onChange={(val) => {
              formik.setFieldValue("payerInsuance", val ?? undefined);
              formik.setFieldValue("insuranceId", val?.insuranceId ?? "");
              // `val` is null when the insurance is cleared, so read it safely
              // instead of dereferencing it.
              formik.setFieldValue(
                "patientInsurance",
                val?.value ? { uuid: val.value } : undefined
              );
            }}
            error={
              formik.touched.payerInsuance &&
              (formik.errors.payerInsuance as any)
            }
          />
        </Grid>
        {/* insuranceId */}
        <Grid item xs={6}>
          <Input
            label="Insurance Id"
            textFieldProps={{
              placeholder: "Enter Insurance Id",
              name: "insuranceId",
              // Same controlled-input guard as Appointment Type above.
              value: formik.values.insuranceId ?? "",
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              disabled: true,
            }}
            error={
              formik.touched.insuranceId && (formik.errors.insuranceId as any)
            }
          />
        </Grid>
        {/* Reason for Claim */}
        <Grid item xs={12}>
          <CustomTextArea
            required
            label="Reason for Visit"
            id="outlined-multiline-static"
            rows={3}
            variant="outlined"
            name="reasonForVisit"
            placeholder="Reason for Visit"
            onBlur={formik.handleBlur}
            value={formik.values?.reasonForVisit ?? ""}
            onChange={formik.handleChange}
            error={
              formik.touched.reasonForVisit &&
              (formik.errors.reasonForVisit as any)
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default UpsertClaim;
